import { useMemo } from "react";
import { FileText, Plus, Printer, Save, Trash2, X } from "lucide-react";
import { useDemoState } from "../demo/DemoStateProvider";
import { useSyncedState } from "../demo/live/useSyncedState";
import { SCRIPT_CATEGORIES, type DemoScript } from "../demo/demo-scripts";
import { track } from "../demo/analytics";
import { cn } from "@/lib/utils";

/**
 * DemoScripts (/scripts) — the session-script library. A prospect can browse the
 * sample scripts, PRINT one (a real window.print of the styled page), and CREATE
 * their own (kept in the in-memory store so it survives tab navigation). All
 * original wording — no branded/proprietary protocol text.
 */
export function DemoScripts() {
  const { scripts, addScript, removeScript } = useDemoState();
  // Co-browsed so the guest sees the same script open + the create/delete UI follow.
  const [activeId, setActiveId] = useSyncedState<string>("scripts.activeId", scripts[0]?.id ?? "");
  const [creating, setCreating] = useSyncedState("scripts.creating", false);
  const [confirmingDelete, setConfirmingDelete] = useSyncedState("scripts.confirmDelete", false);

  const active = scripts.find((s) => s.id === activeId) ?? scripts[0] ?? null;

  function selectScript(id: string) {
    setActiveId(id);
    setCreating(false);
    setConfirmingDelete(false);
  }

  function deleteActive() {
    if (!active) return;
    const remaining = scripts.filter((s) => s.id !== active.id);
    removeScript(active.id);
    setActiveId(remaining[0]?.id ?? "");
    setConfirmingDelete(false);
  }

  // Group scripts by category, preserving the canonical category order.
  const grouped = useMemo(() => {
    const order = [...SCRIPT_CATEGORIES] as string[];
    const cats = Array.from(new Set([...order, ...scripts.map((s) => s.category)]));
    return cats
      .map((c) => ({ category: c, items: scripts.filter((s) => s.category === c) }))
      .filter((g) => g.items.length > 0);
  }, [scripts]);

  function handleCreated(script: DemoScript) {
    addScript(script);
    setActiveId(script.id);
    setCreating(false);
    track("script_created", { category: script.category });
  }

  function printActive() {
    track("script_printed", { id: active?.id ?? "" });
    window.print();
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--color-gold-soft)]">Scripts</p>
          <h1 className="mt-1 font-heading text-3xl font-medium text-[var(--color-text-primary)]">Session Scripts</h1>
          <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">Keep your facilitation scripts on hand, print them for a session, or write your own.</p>
        </div>
        <button
          type="button"
          onClick={() => { setCreating(true); setConfirmingDelete(false); }}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)]"
        >
          <Plus className="h-3.5 w-3.5" /> New script
        </button>
      </header>

      <div className="grid gap-4 lg:grid-cols-[260px_1fr]">
        {/* Script library */}
        <aside className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-navy-mid)] p-3">
          {grouped.map((g) => (
            <div key={g.category} className="mb-3 last:mb-0">
              <p className="px-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[var(--color-text-ghost)]">{g.category}</p>
              {g.items.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => selectScript(s.id)}
                  className={cn(
                    "mt-1 flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-left transition-colors",
                    !creating && active?.id === s.id ? "bg-[var(--color-navy-light)] text-[var(--color-gold-soft)]" : "text-[var(--color-text-muted)] hover:bg-[var(--color-navy-mid)]",
                  )}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    <span className="truncate text-[12px]">{s.name}</span>
                  </span>
                  {s.custom && <span className="shrink-0 rounded border border-[var(--color-border-gold)] px-1 py-0.5 font-mono text-[8px] uppercase tracking-wide text-[var(--color-gold-soft)]">Yours</span>}
                </button>
              ))}
            </div>
          ))}
        </aside>

        {/* Viewer / creator */}
        <div>
          {creating ? (
            <ScriptCreator onCancel={() => setCreating(false)} onCreate={handleCreated} />
          ) : active ? (
            <div>
              <div className="mb-3 flex items-center justify-end gap-2">
                {confirmingDelete ? (
                  <>
                    <span className="mr-auto font-mono text-[11px] uppercase tracking-wide text-[var(--color-danger)]">Delete &ldquo;{active.name}&rdquo;?</span>
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(false)}
                      className="rounded-lg border border-[var(--color-border)] px-3.5 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)]"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={deleteActive}
                      className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-danger)] px-3.5 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-danger)] transition-colors hover:bg-[var(--color-danger)]/15"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(true)}
                      className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] px-3.5 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-danger)] hover:text-[var(--color-danger)]"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                    <button
                      type="button"
                      onClick={printActive}
                      className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border-gold)] px-3.5 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-gold-soft)] transition-colors hover:bg-[var(--color-navy-light)]"
                    >
                      <Printer className="h-3.5 w-3.5" /> Print
                    </button>
                  </>
                )}
              </div>
              <ScriptPaper script={active} />
            </div>
          ) : (
            <p className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-deep)] p-8 text-center text-[13px] text-[var(--color-text-muted)]">No scripts yet — create one to get started.</p>
          )}
        </div>
      </div>
    </main>
  );
}

/** The styled "paper" — also the print target (class `script-print-area`, see app.css @media print). */
function ScriptPaper({ script }: { script: DemoScript }) {
  return (
    <div className="script-print-area mx-auto max-w-[760px] rounded-md bg-white px-10 py-9 text-[#1a1a1a] shadow-xl" style={{ fontFamily: "Georgia, serif", lineHeight: 1.7 }}>
      <p className="text-right text-[12px] text-[#555]">Session script · Psy180</p>
      <h2 className="mt-4 text-center text-[20px] font-bold uppercase">{script.name}</h2>
      <p className="mt-1 text-center text-[11px] uppercase tracking-wide text-[#888]">{script.category}</p>
      {script.body.map((para, i) => (
        <p key={i} className="mt-3 text-[14px]">{para}</p>
      ))}
      <p className="mt-6 text-center font-mono text-[9px] uppercase tracking-wide text-[#999]">
        {script.custom ? "Your script · demo (resets on reload)" : "Original sample wording for demonstration only · not clinical guidance"}
      </p>
    </div>
  );
}

/** Inline "create a script" form — name, category, body. Builds a DemoScript on save. */
function ScriptCreator({ onCancel, onCreate }: { onCancel: () => void; onCreate: (s: DemoScript) => void }) {
  const [name, setName] = useSyncedState("scripts.new.name", "");
  const [category, setCategory] = useSyncedState<string>("scripts.new.category", SCRIPT_CATEGORIES[0]);
  const [bodyText, setBodyText] = useSyncedState("scripts.new.body", "");

  const body = bodyText.split("\n").map((l) => l.trim()).filter(Boolean);
  const canSave = name.trim().length > 0 && body.length > 0;

  function save() {
    if (!canSave) return;
    onCreate({
      id: `custom-${Date.now()}`,
      name: name.trim(),
      category,
      body,
      custom: true,
    });
  }

  return (
    <div className="rounded-2xl border border-[var(--color-border-gold)] bg-[var(--color-navy-mid)] p-5">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-xl font-medium text-[var(--color-text-primary)]">New script</h2>
        <button type="button" onClick={onCancel} aria-label="Cancel" className="rounded-md border border-[var(--color-border)] p-1.5 text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)] hover:text-[var(--color-gold-soft)]">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_180px]">
        <Field label="Script name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Calm Landing" maxLength={80}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2 font-mono text-[13px] text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]" />
        </Field>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2 font-mono text-[13px] text-[var(--color-text-primary)]">
            {SCRIPT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-3">
        <Field label="Script — one line per paragraph">
          <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={10}
            placeholder={"Let your eyes follow the movement…\nNotice what comes up, and let it pass.\nWhen you're ready, take a slow breath."}
            className="w-full resize-y rounded-md border border-[var(--color-border)] bg-[var(--color-bg-deep)] px-3 py-2.5 text-[13px] leading-relaxed text-[var(--color-text-primary)] placeholder:text-[var(--color-text-ghost)]" />
        </Field>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-ghost)]">{body.length} paragraph{body.length === 1 ? "" : "s"}</span>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="rounded-lg border border-[var(--color-border)] px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-gold)]">Cancel</button>
          <button
            type="button"
            onClick={save}
            disabled={!canSave}
            className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-gold)] px-4 py-2 font-mono text-[11px] uppercase tracking-wide text-[var(--color-bg-deep)] transition-colors hover:bg-[var(--color-gold-soft)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Save className="h-3.5 w-3.5" /> Save script
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--color-text-ghost)]">{label}</span>
      {children}
    </label>
  );
}
